if (!customElements.get('bundle-price-stock-check')) {
  customElements.define(
    'bundle-price-stock-check',
    class BundlePriceStockCheck extends HTMLElement {
      constructor() {
        super();

        try {
          /**
           * @type {Bundle}
           */
          this.bundleConfig = JSON.parse(this.dataset.bundleConfig);
          this.sectionId = this.dataset.sectionId;
          this.productId = this.dataset.productId;
          this.badgeColorScheme = this.dataset.badgeColor;
          this.soldOutMessage = this.dataset.soldOutMessage;
        } catch (error) {
          console.error('Invalid bundle configuration.', error);
          return;
        }
      }

      /**
       * @type {number}
       */
      cacheExpirationTime = 1000 * 60 * 2; // 2 minutes

      compressString(input) {
        const dictionary = {};
        const data = (input + '').split('');
        const out = [];
        let currChar;
        let phrase = data[0];
        let code = 256;
        for (let i = 1; i < data.length; i++) {
          currChar = data[i];
          if (dictionary[phrase + currChar] != null) {
            phrase += currChar;
          } else {
            out.push(phrase.length > 1 ? dictionary[phrase] : phrase.charCodeAt(0));
            dictionary[phrase + currChar] = code;
            code++;
            phrase = currChar;
          }
        }
        out.push(phrase.length > 1 ? dictionary[phrase] : phrase.charCodeAt(0));
        for (let i = 0; i < out.length; i++) {
          out[i] = String.fromCharCode(out[i]);
        }
        return out.join('');
      }

      async checkStock() {
        const componentVariantIds = this.bundleConfig.configs[0].components
          .map((component) => component.variantIds)
          .flat();
        let compressedParam = this.compressString(componentVariantIds.join(','));

        // URL encode the compressed string
        compressedParam = encodeURIComponent(compressedParam);

        // Use a common cache key format for all components except bundleCartItem
        const country = window.Shopify.country;
        const cacheKey = `wmxb_bcd_${compressedParam}_${country}`;
        const inProgressKey = `${cacheKey}_in_progress`;

        // Check if we have cached data
        const cachedData = sessionStorage.getItem(cacheKey);
        // Check if there's a request in progress
        const inProgress = window[inProgressKey];
        let components;

        if (cachedData) {
          // Check if the cached data has expired
          const expiration = sessionStorage.getItem(`${cacheKey}_expiration`);
          if (expiration && Date.now() < parseInt(expiration)) {
            // Use cached data if available and not expired
            components = JSON.parse(cachedData);
          } else {
            // If there's a request in progress, wait for it to complete
            if (inProgress) {
              components = await this.waitForCachedData(cacheKey, inProgressKey);
            } else {
              // Mark this request as in progress
              window[inProgressKey] = true;

              try {
                // Fetch new data if cached data has expired
                let response = await fetch(`/a/wmxb/bcd?c=${compressedParam}&country=${country}`);
                components = await response.json();

                // Cache the response for 2 minutes
                sessionStorage.setItem(cacheKey, JSON.stringify(components));

                // Set expiration time (2 minutes)
                const newExpiration = Date.now() + this.cacheExpirationTime;
                sessionStorage.setItem(`${cacheKey}_expiration`, newExpiration.toString());
              } catch (error) {
                console.error('Error fetching bundle component data:', error);
              } finally {
                // Remove the in-progress flag
                window[inProgressKey] = false;
              }
            }
          }
        } else {
          // If there's a request in progress, wait for it to complete
          if (inProgress) {
            components = await this.waitForCachedData(cacheKey, inProgressKey);
          } else {
            // Mark this request as in progress
            window[inProgressKey] = true;

            try {
              // Fetch new data if not in cache
              let response = await fetch(`/a/wmxb/bcd?c=${compressedParam}&country=${country}`);
              components = await response.json();

              // Cache the response for 2 minutes
              sessionStorage.setItem(cacheKey, JSON.stringify(components));

              // Set expiration time (2 minutes)
              const expiration = Date.now() + this.cacheExpirationTime;
              sessionStorage.setItem(`${cacheKey}_expiration`, expiration.toString());
            } catch (error) {
              console.error('Error fetching bundle component data:', error);
            } finally {
              // Remove the in-progress flag
              window[inProgressKey] = false;
            }
          }
        }

        if (!components) {
          console.error('Failed to fetch component data');
          return;
        }

        const outOfStockComponents = components.filter((component) => {
          const requiredQuantity = this.bundleConfig.configs[0].components.find(
            (configComponent) => configComponent.productId === component.id
          ).quantityOptions[0].quantity;

          return component.variants.every((variant) => {
            const lessThanRequiredQty = variant.inventoryQuantity < requiredQuantity;
            const denySalesWhenOutOfStock = variant.inventoryPolicy === 'deny';
            const inventoryIsTracked =
              variant.inventoryItem &&
              typeof variant.inventoryItem.tracked === 'boolean' &&
              variant.inventoryItem.tracked === true;
            return (lessThanRequiredQty && denySalesWhenOutOfStock && inventoryIsTracked) === true;
          });
        });

        if (outOfStockComponents.length > 0) {
          const badge = document.createElement('span');
          badge.id = `BundlePriceSoldOutBadge-${this.sectionId}-${this.productId}`;
          badge.classList.add('badge', 'price__badge-sold-out', `color-${this.badgeColorScheme}`);
          badge.textContent = this.soldOutMessage;
          this.appendChild(badge);
        }
      }

      /**
       * Wait for cached data to be available
       * @param {string} cacheKey - The cache key to check
       * @param {string} inProgressKey - The in-progress key to check
       * @param {number} maxWaitTime - Maximum wait time in milliseconds
       * @param {number} checkInterval - Interval between checks in milliseconds
       * @returns {Promise<Array>}
       */
      async waitForCachedData(cacheKey, inProgressKey, maxWaitTime = 5000, checkInterval = 100) {
        const startTime = Date.now();

        while (Date.now() - startTime < maxWaitTime) {
          // Check if the data is now available
          const cachedData = sessionStorage.getItem(cacheKey);
          const stillInProgress = window[inProgressKey];

          if (cachedData) {
            // Data is available, use it
            return JSON.parse(cachedData);
          }

          if (!stillInProgress) {
            // The request is no longer in progress but data is not available
            // This likely means the request failed
            break;
          }

          // Wait before checking again
          await new Promise((resolve) => setTimeout(resolve, checkInterval));
        }

        // If we get here, either the wait timed out or the request failed
        // Proceed with our own request
        try {
          const componentVariantIds = this.bundleConfig.configs[0].components
            .map((component) => component.variantIds)
            .flat();
          let compressedParam = this.compressString(componentVariantIds.join(','));
          compressedParam = encodeURIComponent(compressedParam);
          const country = window.Shopify.country;

          window[inProgressKey] = true;

          let response = await fetch(`/a/wmxb/bcd?c=${compressedParam}&country=${country}`);
          const components = await response.json();

          // Cache the response
          sessionStorage.setItem(cacheKey, JSON.stringify(components));

          // Set expiration time (2 minutes)
          const expiration = Date.now() + this.cacheExpirationTime;
          sessionStorage.setItem(`${cacheKey}_expiration`, expiration.toString());

          return components;
        } catch (error) {
          console.error('Error fetching bundle component data after waiting:', error);
          return null;
        } finally {
          // Remove the in-progress flag
          window[inProgressKey] = false;
        }
      }

      connectedCallback() {
        this.checkStock();
      }
    }
  );
}
